import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Box, CircularProgress, Alert, Avatar, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowUp } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { chatTheme } from '../../theme/chatTheme';
import AIChatMessage from './AIChatMessage';
import AIChatInput from './AIChatInput';

// Custom hook to safely use business context (may not be available for couples)
// Matches the pattern used in ChatContainer.js
const useBusinessSafe = () => {
  try {
    const BusinessContextModule = require('../../context/BusinessContext');
    const BusinessContext = BusinessContextModule?.BusinessContext;
    
    // Ensure BusinessContext is a valid React context before using it
    if (!BusinessContext || typeof BusinessContext !== 'object' || !BusinessContext.Provider) {
      return { selectedBusiness: null };
    }
    
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const context = useContext(BusinessContext);
    return context || { selectedBusiness: null };
  } catch (error) {
    return { selectedBusiness: null };
  }
};

/**
 * AIChatContainer - Reusable AI chat component for cross-app support
 * 
 * Supports both couple and supplier modes:
 * - Couple mode: Uses /api/ai/couple-chat with wedding context
 * - Supplier mode: Uses /api/ai/supplier-chat with business/wedding context
 * 
 * @param {Object} props
 * @param {string} props.mode - 'couple' | 'supplier'
 * @param {string} props.weddingId - Optional wedding ID (for couple mode or supplier wedding mode)
 * @param {string} props.businessId - Optional business ID (for supplier mode)
 * @param {string} props.supplierMode - 'business' | 'wedding' (for supplier mode only)
 * @param {string} props.chatId - Optional existing chat ID to continue conversation
 * @param {Function} props.onChatCreated - Callback when new chat is created
 */
const AIChatContainer = ({
  mode = 'couple',
  weddingId = null,
  businessId = null,
  businessLoading = false, // Loading state for business context
  supplierMode = 'business', // 'business' or 'wedding'
  chatId = null,
  onChatCreated = null,
  onChatChanged = null,
  onChatUpdated = null,
  onCreditsUpdated = null // Callback when credits are updated
}) => {
  const { user, wedding } = useAuth();
  const { selectedBusiness } = useBusinessSafe();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [errorUpgradeRequired, setErrorUpgradeRequired] = useState(false);
  const navigate = useNavigate();
  const [isFunctionCalling, setIsFunctionCalling] = useState(false);
  const [functionCallName, setFunctionCallName] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(chatId);
  const [titleGenerated, setTitleGenerated] = useState(false);
  const messagesEndRef = useRef(null);

  // Determine active IDs
  const activeWeddingId = weddingId || wedding?._id;
  const activeBusinessId = businessId || selectedBusiness?.business?._id;

  // Update currentChatId when chatId prop changes
  useEffect(() => {
    if (chatId !== currentChatId) {
      setCurrentChatId(chatId);
      if (onChatChanged) {
        onChatChanged(chatId);
      }
    }
  }, [chatId, currentChatId, onChatChanged]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load existing chat messages when chatId changes
  useEffect(() => {
    if (currentChatId) {
      loadChatMessages(currentChatId);
      // Reset title generated flag when switching chats
      setTitleGenerated(false);
    } else {
      // Clear messages when no chat is selected
      setMessages([]);
      setTitleGenerated(false);
    }
  }, [currentChatId]);

  const loadChatMessages = async (chatIdToLoad) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/chat/conversations/${chatIdToLoad}/messages`);
      setMessages(response.data || []);
    } catch (err) {
      setError('Failed to load chat history');
      setErrorUpgradeRequired(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = useCallback(async (content) => {
    if (!content.trim() || sending) return;

    setSending(true);
    setError('');
    setErrorUpgradeRequired(false);
    setIsFunctionCalling(false);
    setFunctionCallName(null);

    // Add user message optimistically
    const tempUserMessage = {
      _id: `temp-${Date.now()}`,
      sender: user._id,
      senderType: mode === 'couple' ? 'couple' : 'supplier',
      content: content.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      let endpoint;
      let requestBody;

      if (mode === 'couple') {
        // Couple chat endpoint
        endpoint = '/api/ai/couple-chat';
        requestBody = {
          message: content.trim(),
          weddingId: activeWeddingId,
          chatId: currentChatId
        };
      } else {
        // Supplier chat endpoint
        endpoint = '/api/ai/supplier-chat';
        requestBody = {
          message: content.trim(),
          mode: supplierMode,
          businessId: activeBusinessId,
          weddingId: supplierMode === 'wedding' ? activeWeddingId : undefined,
          chatId: currentChatId
        };
      }

      const response = await api.post(endpoint, requestBody);

      // Update chat ID if new chat was created
      if (response.data.chatId && response.data.chatId !== currentChatId) {
        setCurrentChatId(response.data.chatId);
        setTitleGenerated(false); // Reset title generation flag for new chat
        if (onChatCreated) {
          onChatCreated(response.data.chatId);
        }
        if (onChatChanged) {
          onChatChanged(response.data.chatId);
        }
      }

      // Handle function calling indicator
      if (response.data.functionCalling) {
        setIsFunctionCalling(true);
        setFunctionCallName(response.data.functionName || null);
      }

      // Replace temp message and add AI response
      setMessages(prev => {
        const filtered = prev.filter(m => !m._id?.toString().startsWith('temp-'));
        const newMessages = response.data.messages || [];
        
        // Add token usage info to last AI message if available
        if (newMessages.length > 0 && response.data.creditsDeducted) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage.senderType === 'ai') {
            lastMessage.tokenInfo = {
              creditsDeducted: response.data.creditsDeducted,
              tokensUsed: response.data.tokensUsed,
              remainingCredits: response.data.remainingCredits
            };
          }
          // Notify parent component to reload credits (defer to avoid React warning)
          if (onCreditsUpdated) {
            setTimeout(() => {
              onCreditsUpdated();
            }, 0);
          }
        }
        
        const updatedMessages = [...filtered, ...newMessages];
        
        // Auto-generate title after first user message if not already generated
        if (currentChatId && !titleGenerated) {
          const userMessages = updatedMessages.filter(m => m.senderType !== 'ai' && !m._id?.toString().startsWith('temp-'));
          if (userMessages.length >= 1) {
            // Generate title asynchronously (don't block UI)
            setTitleGenerated(true); // Set flag immediately to prevent duplicate calls
            api.post(`/api/chat/conversations/${currentChatId}/generate-title`)
              .then(() => {
                if (onChatUpdated) {
                  onChatUpdated(currentChatId);
                }
              })
              .catch(_err => {
              setTitleGenerated(false); // Reset flag on error so it can retry
              // Don't show error to user, just log it
            });
          }
        }
        
        return updatedMessages;
      });

      // Clear function calling after a delay
      if (response.data.functionCalling) {
        setTimeout(() => {
          setIsFunctionCalling(false);
          setFunctionCallName(null);
        }, 2000);
      }
    } catch (err) {
      // Handle insufficient credits error
      if (err.response?.status === 402 || err.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        setError('Insufficient AI credits. Please purchase more credits to continue.');
        setErrorUpgradeRequired(false);
      } else if (err.response?.status === 429 || err.response?.data?.code === 'MESSAGE_LIMIT_EXCEEDED' || err.response?.data?.upgradeRequired) {
        setError(err.response?.data?.message || 'AI message limit reached. Upgrade your plan for more messages.');
        setErrorUpgradeRequired(true);
      } else {
        setError(err.response?.data?.message || 'Failed to send message');
        setErrorUpgradeRequired(false);
      }

      // Remove temp message on error
      setMessages(prev => prev.filter(m => !m._id?.toString().startsWith('temp-')));
    } finally {
      setSending(false);
    }
  }, [mode, supplierMode, activeWeddingId, activeBusinessId, currentChatId, user, onChatCreated, onChatChanged, onChatUpdated, onCreditsUpdated, titleGenerated, sending]);

  // Validation
  if (mode === 'couple' && !activeWeddingId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          Please select a wedding to start chatting with Lumi.
        </Alert>
      </Box>
    );
  }

  // Show loading state while business is loading
  if (mode === 'supplier' && businessLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading business information...
        </Typography>
      </Box>
    );
  }

  // Only show error if business has finished loading and there's still no business
  if (mode === 'supplier' && !businessLoading && !activeBusinessId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          Please select a business to start chatting with Lumi.
        </Alert>
      </Box>
    );
  }

  if (mode === 'supplier' && supplierMode === 'wedding' && !activeWeddingId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          Please select a wedding to chat about.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
        minHeight: 0,
        overflow: 'hidden',
        '@keyframes typingBounce': {
          '0%, 80%, 100%': {
            transform: 'scale(0)',
            opacity: 0.5,
          },
          '40%': {
            transform: 'scale(1)',
            opacity: 1,
          },
        },
      }}
    >
      {/* Messages area */}
      <Box sx={chatTheme.messageList}>
        {loading && messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
            <p>Start a conversation with Lumi!</p>
            <p style={{ fontSize: '0.875rem', marginTop: '8px' }}>
              Ask questions about your {mode === 'couple' ? 'wedding planning' : 'business'} or get help with tasks.
            </p>
          </Box>
        ) : (
          <>
            {messages.map((message) => {
              // Determine if message is from current user
              const isOwnMessage = message.senderType !== 'ai' && 
                (message.sender?.toString() === user._id || 
                 message.sender?._id?.toString() === user._id);
              
              return (
                <AIChatMessage
                  key={message._id}
                  message={message}
                  isOwnMessage={isOwnMessage}
                  userType={mode}
                  user={user}
                  selectedBusiness={selectedBusiness}
                />
              );
            })}
            
            {/* Typing indicator when AI is responding */}
            {sending && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-start',
                  mb: 2,
                  gap: 1,
                }}
              >
                <Avatar
                  src="/img/lumi.png"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#fff',
                    flexShrink: 0,
                    padding: '5px',
                    '& img': {
                      objectFit: 'contain',
                    },
                  }}
                />
                <Box
                  sx={{
                    ...chatTheme.messageBubble.ai,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={chatTheme.typingIndicator}>
                    <Box component="span" />
                    <Box component="span" />
                    <Box component="span" />
                  </Box>
                </Box>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            onClose={() => { setError(''); setErrorUpgradeRequired(false); }}
            action={errorUpgradeRequired && mode === 'supplier' ? (
              <Button color="inherit" size="small" startIcon={<ArrowUp size={16} />} onClick={() => navigate('/supplier/billing')}>
                Upgrade
              </Button>
            ) : null}
          >
            {error}
          </Alert>
        )}
      </Box>

      {/* Input area - fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <AIChatInput
          onSend={handleSendMessage}
          disabled={sending || loading}
          isFunctionCalling={isFunctionCalling}
          functionCallName={functionCallName}
          placeholder={mode === 'couple' ? 'Ask Lumi about your wedding...' : 'Ask Lumi about your business...'}
        />
      </Box>

      {/* AI disclaimer — below chat panel (right column in Lumi layout) */}
      <Box
        component="p"
        sx={{
          flexShrink: 0,
          m: 0,
          px: 1.5,
          py: 1,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          borderTop: '1px solid',
          borderColor: 'divider',
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        Lumi is an AI assistant. It may make mistakes or misinterpret information. Please verify important details directly in your messages or agreements.
      </Box>
    </Box>
  );
};

export default AIChatContainer;

